import { useMemo, useState } from 'react';
import { admin } from '@repletoryreach/sdk-api';
import { Stack, Group, Button, Text, Badge, SimpleGrid } from '@repletoryreach/sdk-ui';
import { SegmentedControl } from '@repletoryreach/sdk-ui/inputs';
import { Alert, DataTable, EmptyState, PageHeader, StatusBadge, TableShell } from '@repletoryreach/sdk-ui/data-display';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useBlogPosts, usePublishBlogPost, useDeleteBlogPost } from '../../../data/useBlog';
import { PostEditorDrawer } from './components/PostEditorDrawer';
import { StatCard } from '../../../components/StatCard';
import { TwoLineCell } from '../../../components/TwoLineCell';
import { BillingToolbar } from '../../billing/components/BillingToolbar';
import { IconLayers, IconClock, IconTag } from '../../billing/components/icons';
import { formatDate, formatInteger, formatRelative } from '../../../lib/format';
import { usePersistedState } from '../../../lib/usePersistedState';
import type { Indexed } from '../../../lib/types';
import { useT } from '../../../i18n/I18nProvider';
import './blog.css';

const STAT_COLS = { base: 1, sm: 3 };
const API_BASE = ((import.meta.env.VITE_API_BASE as string | undefined) ?? '/api').replace(/\/+$/, '');

/** Where a cover comes from: an uploaded file, a site illustration, or a full address. */
function coverSrc(cover: string | null): string | null {
  if (!cover) return null;
  const m = /^asset:([0-9a-fA-F-]{36})$/.exec(cover);
  if (m) return `${API_BASE}/v1/public/blog/assets/${m[1]}`;
  return /^https?:\/\//.test(cover) ? cover : null;
}

/**
 * The marketing site's news and updates: product news, price changes,
 * announcements and guides, written here and published to the site the
 * moment you say so.
 */
export default function BlogPage() {
  const t = useT();
  const postsQ = useBlogPosts();
  const publish = usePublishBlogPost();
  const del = useDeleteBlogPost();
  const [editing, setEditing] = useState<admin.BlogPost | 'new' | null>(null);
  const [status, setStatus] = usePersistedState<string>('admin.blog.status', 'all');
  const [query, setQuery] = useState('');

  const all = useMemo(() => postsQ.data?.data ?? [], [postsQ.data]);
  const counts = useMemo(() => ({
    published: all.filter((p) => p.status === 'published').length,
    draft: all.filter((p) => p.status === 'draft').length,
    categories: new Set(all.map((p) => p.category)).size
  }), [all]);

  const rows = useMemo(() => {
    let out = all;
    if (status !== 'all') out = out.filter((p) => p.status === status);
    const q = query.trim().toLowerCase();
    if (q) out = out.filter((p) => p.title.toLowerCase().includes(q) || p.category.toLowerCase().includes(q) || p.slug.includes(q));
    return out;
  }, [all, status, query]);

  function toggle(post: admin.BlogPost) {
    const publishing = post.status !== 'published';
    openConfirmModal({
      title: publishing ? t('content.blog.confirm.publishTitle', { title: post.title }) : t('content.blog.confirm.unpublishTitle', { title: post.title }),
      children: <Text role="bodySmall">{publishing ? t('content.blog.confirm.publishBody') : t('content.blog.confirm.unpublishBody')}</Text>,
      labels: { confirm: publishing ? t('content.blog.action.publish') : t('content.blog.action.unpublish'), cancel: t('action.cancel') },
      onConfirm: () => publish.mutate({ id: post.id, publish: publishing }, {
        onSuccess: () => notifySuccess(publishing ? t('content.blog.toast.published', { title: post.title }) : t('content.blog.toast.unpublished', { title: post.title }), t('content.blog.title')),
        onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('content.blog.title'))
      })
    });
  }

  function remove(post: admin.BlogPost) {
    openConfirmModal({
      title: t('content.blog.confirm.deleteTitle', { title: post.title }),
      children: <Text role="bodySmall">{t('content.blog.confirm.deleteBody')}</Text>,
      labels: { confirm: t('action.delete'), cancel: t('action.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => del.mutate(post.id, {
        onSuccess: () => notifySuccess(t('content.blog.toast.deleted', { title: post.title }), t('content.blog.title')),
        onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('content.blog.title'))
      })
    });
  }

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('content.blog.eyebrow')}
        title={t('content.blog.title')}
        description={t('content.blog.description')}
        actions={<Button size="sm" onClick={() => setEditing('new')}>{t('content.blog.action.new')}</Button>}
      />

      <SimpleGrid cols={STAT_COLS} spacing="md">
        <StatCard withRule tone="success" icon={<IconLayers />} label={t('content.blog.stat.published')} value={formatInteger(counts.published)} hint={t('content.blog.stat.publishedHint')} loading={postsQ.isLoading} />
        <StatCard withRule tone="warning" icon={<IconClock />} label={t('content.blog.stat.drafts')} value={formatInteger(counts.draft)} hint={t('content.blog.stat.draftsHint')} loading={postsQ.isLoading} />
        <StatCard withRule tone="info" icon={<IconTag />} label={t('content.blog.stat.categories')} value={formatInteger(counts.categories)} hint={t('content.blog.stat.categoriesHint')} loading={postsQ.isLoading} />
      </SimpleGrid>

      <BillingToolbar search={query} onSearch={setQuery} searchPlaceholder={t('content.blog.search')}>
        <SegmentedControl
          size="xs"
          value={status}
          onChange={setStatus}
          data={[
            { value: 'all', label: t('content.blog.filter.all') },
            { value: 'published', label: t('content.blog.status.published') },
            { value: 'draft', label: t('content.blog.status.draft') }
          ]}
        />
      </BillingToolbar>

      <TableShell
        totalLabel={t('content.blog.showingCount', { shown: formatInteger(rows.length), total: formatInteger(all.length) })}
        loading={postsQ.isLoading}
        isEmpty={rows.length === 0}
        empty={<EmptyState title={t('content.blog.empty')} description={all.length === 0 ? t('content.blog.emptyHint') : t('common.tryClearingFilters')} action={all.length === 0 ? <Button size="xs" onClick={() => setEditing('new')}>{t('content.blog.action.new')}</Button> : undefined} />}
        error={postsQ.error ? (
          <Alert color="error">
            <Stack gap="sm">
              <Text role="bodySmall">{t('content.blog.loadError')}</Text>
              <Group><Button size="xs" variant="outline" onClick={() => void postsQ.refetch()}>{t('action.retry')}</Button></Group>
            </Stack>
          </Alert>
        ) : null}
      >
        <DataTable<Indexed<admin.BlogPost>>
          records={rows as Indexed<admin.BlogPost>[]}
          loading={postsQ.isLoading}
          idAccessor="id"
          emptyState={t('content.blog.empty')}
          columns={[
            {
              accessor: 'title',
              title: t('content.blog.columns.story'),
              sortable: true,
              render: (r) => {
                const src = coverSrc(r.cover);
                return (
                  <TwoLineCell
                    leading={src ? <img className="rr-blog-thumb" src={src} alt="" /> : <span className="rr-blog-thumb" aria-hidden />}
                    primary={r.title}
                    secondary={<>/blog/{r.slug} · {t('content.blog.readMinutes', { count: r.read_minutes })}</>}
                    title={r.title}
                  />
                );
              }
            },
            { accessor: 'category', title: t('content.blog.columns.category'), sortable: true, render: (r) => <Badge size="xs" variant="light" color="blue">{r.category}</Badge> },
            {
              accessor: 'status',
              title: t('common.status'),
              sortable: true,
              render: (r) => {
                // A published story dated later stays off the site until its date.
                const scheduled = r.status === 'published' && r.published_at !== null && r.published_at !== undefined && new Date(r.published_at).getTime() > Date.now();
                return <StatusBadge status={r.status === 'published' && !scheduled ? 'healthy' : 'pending'}>{scheduled ? t('content.blog.status.scheduled') : r.status === 'published' ? t('content.blog.status.published') : t('content.blog.status.draft')}</StatusBadge>;
              }
            },
            {
              accessor: 'published_at',
              title: t('content.blog.columns.published'),
              sortable: true,
              render: (r) => r.published_at ? <span title={r.published_at}><TwoLineCell primary={formatDate(r.published_at)} secondary={formatRelative(r.published_at)} /></span> : <Text role="caption" c="var(--rr-color-text-muted)">{t('content.blog.notYet')}</Text>
            },
            {
              accessor: 'updated_at',
              title: t('common.updated'),
              sortable: true,
              render: (r) => <TwoLineCell primary={formatRelative(r.updated_at)} secondary={r.updated_by ?? ''} />
            },
            {
              accessor: 'actions',
              title: t('common.actions'),
              textAlign: 'right',
              render: (r) => (
                <Group gap="xs" justify="flex-end" wrap="nowrap">
                  <Button size="xs" variant="outline" onClick={() => setEditing(r)}>{t('action.edit')}</Button>
                  <Button size="xs" variant={r.status === 'published' ? 'subtle' : 'filled'} onClick={() => toggle(r)}>{r.status === 'published' ? t('content.blog.action.unpublish') : t('content.blog.action.publish')}</Button>
                  <Button size="xs" variant="subtle" color="red" onClick={() => remove(r)}>{t('action.delete')}</Button>
                </Group>
              )
            }
          ]}
        />
      </TableShell>

      <PostEditorDrawer post={editing} opened={editing !== null} onClose={() => setEditing(null)} />
    </Stack>
  );
}
